import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Role } from '../../models/role.model';
import { MatDialog } from '@angular/material/dialog';
import { RoleMembershipDialog } from '../../dialogs/role-membership/role-membership.dialog';
import { RoleDetailsDialog } from '../../dialogs/role-details/role-details.dialog';
import { Folder } from '../../models/site-content/folder.model';
import { RoleService } from '../../services/role.service';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { FolderService } from '../../services/folder.service';
import { AccessibilityHelper } from '../../accessibility.helper';
import { Logging } from '../../logging';
import { RolesWrapper } from '../../models/roles-wrapper.model';
import { CurrentSiteService } from '../../services/current-site.service';
import { IDrawerComponentLiaison } from '../../interfaces/idrawer-component-liaison';
import { DrawerComponentBase } from '../drawer-component-base/drawer-component-base.component';

@Component({
    selector: 'app-role-viewer',
    templateUrl: './role-viewer.component.html',
    styleUrls: ['./role-viewer.component.scss'],
    standalone: false
})
export class RoleViewerComponent extends DrawerComponentBase implements OnInit {
    // Inputs.
    @Input() roles: Role[];
    @Input() membersAreInherited: boolean;
    @Input() folderId: number;
    @Input() enableInheritanceControlButton: boolean;
    @Input() folder: Folder;
    @Input() title: string;
    @Input() siteId: number;
    @Input() folderOrSitelevel: string;
    @Input() rolesWrapper: RolesWrapper;
    @Input() drawerComponentLiaison: IDrawerComponentLiaison = null;

    @Output() close: EventEmitter<Folder> = new EventEmitter();

    rolesWithMembers: Role[];
    rolesWithNoMembers: Role[];
    readonly ROLES_WITH_NO_MEMBERS_THRESHOLD: number = 2;

    constructor(
        public dialog: MatDialog,
        private roleService: RoleService,
        private folderService: FolderService,
        private currentSiteService: CurrentSiteService
    ) {
        super();
    }

    ngOnInit() {
        super.ngOnInit(); // Important to call this as the first line in this method.

        this.updateRoles();
    }

    updateRoles(roles: Role[] = null) {
        if (roles) {
            this.roles = roles;
        }
        let compareRoles = function (a, b) { return a.name > b.name ? 1 : -1 };
        this.rolesWithMembers = this.roles.filter(r => { return r.memberCount() > 0 });
        this.rolesWithNoMembers = this.roles.filter(r => { return r.memberCount() == 0 });
        this.roles = this.rolesWithMembers.concat(this.rolesWithNoMembers);
    }

    getInheritanceTooltip() {
        if (!this.enableInheritanceControlButton) {
            return "";
        } else if (this.membersAreInherited) {
            return "Click this if you wish to manage Role membership directly for this Folder rather than inherit Role members fron the parent Folder";
        } else {
            return "Click this if you wish to stop managing Role membership for this Folder directly instead inherit Role members from the parent Folder";
        }
    }

    toggleInheritance() {
        if (!this.enableInheritanceControlButton) return;

        this.changeMemberInheritanceOnConfirm(() => {
            let newInheritedState = !this.membersAreInherited;

            if (newInheritedState) {
                this.folderService.deleteRoleGroup(this.folderId)
                    .then(x => {
                        this.roleService.getFolderRoles(this.folderId).then(response => {
                            this.updateRoles(response.roles);
                            this.membersAreInherited = newInheritedState;
                            this.folder.roleMembersAreInherited = true;
                        });
                    });
            } else {
                // TODO - avoid this double hit to the server -- createRoleGroup will
                // need to return an object with a roles member
                this.folderService.createRoleGroup(this.folderId, []).then(response => {
                    this.roleService.getFolderRoles(this.folderId).then(response => {
                        this.updateRoles(response.roles);
                    });
                    this.membersAreInherited = newInheritedState;
                    this.folder.roleMembersAreInherited = false;
                });
            }
        });
    }

    onClose(): void {
        console.log('RoleViewer.onClose()');
        this.close.emit(this.folder);
    }

    hasEmptyRoles() {
        return this.rolesWithNoMembers.length > 0;
    }

    public get SiteLevelRoles(): Role[] {
        return this.roles.filter(x => { return x.isSiteLevel });
    }

    public get FolderLevelRoles(): Role[] {
        return this.roles.filter(x => { return !x.isSiteLevel });
    }

    public get FolderIsManagingOwnRolesOrInheritingFromAncestor(): boolean {
        return (!this.membersAreInherited) || (this.rolesWrapper.folderFromWhichRolesAreInherited != null);
    }

    public openRoleDialog(data: any): void {
        if (this.membersAreInherited || data.role.canGrant == false) return;
        AccessibilityHelper.preventDoubleHitOnEvent(data.event);
        data.event.stopPropagation();
        const dialogRef = this.dialog.open(RoleMembershipDialog, {
            width: '800px',
            data: {
                role: data.role,
                roleId: data.role.id,
                siteId: this.siteId,
                roleName: data.role.name,
                entityName: data.entityName,
                members: data.role.members,
                folderId: this.folderId,
                folderOrSitelevel: this.folderOrSitelevel,
                membersAreInherited: this.membersAreInherited
            }
        });

        dialogRef.afterClosed().subscribe(data => {
            if (data === 'save') {
                this.roleService.getFolderRoles(this.folderId).then(response => {
                    if (this.folderView()) {
                        this.membersAreInherited = response.membersAreInherited;
                    }
                    this.updateRoles(response.roles);
                });
            }
            AccessibilityHelper.setFocus('.close-btn');
        });
    }

    // Returns true if the roles are being managed at the site level rather than at a folder level
    public adminView(): boolean {
        return this.folderOrSitelevel == 'site';
    }

    // Returns true if the roles are being managed at the folder level rather than at the site level
    public folderView(): boolean {
        return this.folderOrSitelevel == 'folder';
    }

    public getTitleForManageRolesIcon() {
        if (this.membersAreInherited) {
            return "Role members are inherited from parent folder - you'll need to manage role membership there.";
        } else {
            return "Manage Role Members";
        }
    }

    public openEditRoleDialog(data: any) {
        let origIsSiteLevel = data.role.isSiteLevel;
        const dialogRef = this.dialog.open(RoleDetailsDialog, {
            data: {
                role: data.role
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(role => {
            if (role != null) {
                this.doUpdateRole(role);
            }
        });
    }

    private doUpdateRole(role: any) {
        this.roleService.update(role).then(res => {
            let roles = this.roles.filter(x => x.id != res.id);
            //roles.push(res);
            //this.roles = Object.assign([], roles);
            this.currentSiteService.updateRole(role);
        });
    }

    public openCreateRoleDialog() {
        const dialogRef = this.dialog.open(RoleDetailsDialog);
        dialogRef.afterClosed().subscribe(newRole => {
            if (newRole != null) {
                if (newRole.isSiteLevel) {
                    if (this.SiteLevelRoles.filter(r => r.name == newRole.name).length > 0) {
                        return;
                    }
                }
                else {
                    if (this.FolderLevelRoles.filter(r => r.name == newRole.name).length > 0) {
                        return;
                    }
                }

                newRole.dataCollectionId = this.siteId;
                this.roleService.create(newRole).then(res => {
                    this.roles.push(res);
                    this.currentSiteService.addRole(res as Role);
                });
            }
        });
    }

    public deleteRole(event: any, role: any) {
        this.deleteOnConfirm(role, () => {
            this.roleService.delete(role.id).then(res => {
                this.roles = this.roles.filter(r => r.id != role.id);
                this.currentSiteService.deleteRole(role);
            });
        });
    }

    private changeMemberInheritanceOnConfirm(callback: Function): void {
        let message: string;
        if (this.membersAreInherited) {
            message = 'If you continue, role membership for this folder will no longer be inherited from the parent folder. Role membership will need to be managed here. Are you sure you wish to continue?';
        } else {
            message = 'If you continue, role membership for this folder will be inherited from the parent folder. Are you sure you wish to continue?';
        }
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Permissions Inheritance`, message)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) callback();
        });
    }

    // TO DO: create a DialogService and put this in it
    private deleteOnConfirm(role: Role, callback: Function) {
        if (role.memberCount() > 0) {
            alert("You cannot delete a Role that has members. Remove the members from this role first, then delete it.");
            return;
        }
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Delete Role`, `Are you sure you want to delete ${role.name} Role? The operation cannot be undone.`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) callback();
        });
    }
}
